import React, { useContext, useEffect, useState, useRef } from 'react'

import AuthContext from '../../context/AuthContext';
import ModalContext from '../../context/ModalContext';
import DrawerContext from '../../context/DrawerContext';
import PageSearchBar from '../../common/PageSearchBar';
import "./CouponLeads.scss";

//MUI
import { MoreVertRounded } from '@mui/icons-material';
import { Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Fade from '@mui/material/Fade';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Status filter buttons shown at the top of the page.
const STATUS_FILTERS = [
  { key: 'draft', label: 'Draft' },
  { key: 'pending', label: 'Pending' },
  { key: 'paid', label: 'Paid' },
  { key: 'live', label: 'Live' },
];

// Lead type filter buttons. Note: the plain lead is shown as "Inquiry" in the UI
// but is still sent/received as `lead` from the backend.
const TYPE_FILTERS = [
  { key: 'coupon', label: 'Coupon' },
  { key: 'popin', label: 'Popin' },
  { key: 'popin_coupon', label: 'Popin + Coupon' },
  { key: 'lead', label: 'Inquiry' },
];

// Initials avatar shown when a company has no logo image (or it fails to load).
function TempLogo({ name }) {
  let initials = '';
  if (!name || name.trim().toUpperCase() === 'N/A') {
    initials = 'N/A';
  } else {
    initials = name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }

  return (
    <div
      style={{
        height: '32px',
        width: '32px',
        borderRadius: '100%',
        backgroundColor: '#576574',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '12px',
        fontWeight: 'bold',
      }}
    >
      {initials}
    </div>
  );
}

const normalizeText = (v) => (v || '').toString().toLowerCase().trim();
const normalizeCompact = (v) => normalizeText(v).replace(/[^a-z0-9]/g, '');

const normalizeWebsite = (v) =>
  normalizeText(v).replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');

const getRowKey = (row) => `${row?.type || 'lead'}-${row?.id}`;

const getLeadSearchFields = (row) => ({
  name: normalizeCompact(row?.companyName || row?.company_name),
  email: normalizeText(row?.email),
  website: normalizeWebsite(row?.website),
});

const editDistance = (a, b) => {
  const m = a.length, n = b.length;
  if (!m) return { full: n, prefix: 0 };
  if (!n) return { full: m, prefix: m };
  let prev2 = null;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const curr = [i];
    for (let j = 1; j <= n; j++) {
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        curr[j] = Math.min(curr[j], prev2[j - 2] + 1);
      }
    }
    prev2 = prev;
    prev = curr;
  }
  return { full: prev[n], prefix: Math.min(...prev) };
};

const exactMatch = (query, target) => !!query && !!target && target.includes(query);

const isFuzzyMatch = (query, target) => {
  if (!query || !target) return false;
  if (target.includes(query)) return true;
  if (query.includes(target) && target.length >= 4 && target.length * 2 >= query.length) return true;
  const maxDist = query.length >= 8 ? 2 : query.length >= 4 ? 1 : 0;
  if (!maxDist) return false;
  return editDistance(query, target).prefix <= maxDist;
};

// Pulls the best-available company logo URL off a normalized lead row.
// Pop-in and coupon rows carry an `advertising` object with sized logos; plain
// inquiry leads have none, so this returns null and the initials avatar is used.
const getCompanyLogoUrl = (row) => {
  const ad = row?.advertising || {};
  return ad.logo_md || ad.logo_sm || ad.logo_lg || ad.logo_xs || ad.logo_xlg || null;
};

// Renders the company logo image, falling back to the initials avatar when there
// is no logo or the image fails to load.
function CompanyLogo({ logoUrl, name }) {
  const [errored, setErrored] = useState(false);

  if (logoUrl && !errored) {
    return (
      <img
        src={logoUrl}
        alt={name || 'Company logo'}
        onError={() => setErrored(true)}
        style={{
          height: '32px',
          width: '32px',
          borderRadius: '100%',
          objectFit: 'cover',
        }}
      />
    );
  }

  return <TempLogo name={name} />;
}

export default function CouponLeads() {

  const modalCtx = useContext(ModalContext);
  const drawerCtx = useContext(DrawerContext);
  const authCtx = useContext(AuthContext);

  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [couponsData, setCouponsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [displayedData, setDisplayedData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  // fuzzy: results are near-matches only (no exact hit); suggestions: "did you
  // mean" values; related: rowKey -> 'email' | 'website' for rows pulled in
  // because they share an email/website with a matched company.
  const [searchMeta, setSearchMeta] = useState({ fuzzy: false, suggestions: [], related: {} });
  const [loadingView, setLoadingView] = useState(false);
  const [checkingMakeLive, setCheckingMakeLive] = useState(false);
  const [markingContacted, setMarkingContacted] = useState(false);

  // Status filter is multi-select (empty = all). Default to showing only Paid leads.
  const [statusFilter, setStatusFilter] = useState(['paid']);
  const [typeFilter, setTypeFilter] = useState([]);

  // Infinite scroll.
  const [visibleItems, setVisibleItems] = useState(20);
  const tableContainerRef = useRef(null);

  const normalizeLeadRow = (row) => {
    if (!row || typeof row !== 'object') return row;
    if (row.entity && typeof row.entity === 'object') {
      return { id: row.id, type: row.type, ...row.entity };
    }
    return row;
  };

  const getRowTypeLabel = (row) => {
    if (!row) return null;
    if (row.type === 'coupon') return 'Coupon';
    if (row.type === 'popin') return row.coupon_id ? 'Popin + Coupon' : 'Popin';
    return 'Inquiry';
  };

  // Bucket a row into one of the four status filters.
  const getStatusKey = (row) => {
    if (!row) return null;
    // Coupons go live as 'approved'; pop-ins go live as 'active'.
    if (row.status === 'approved') return 'live';
    if (row.type === 'popin' && row.status === 'active') return 'live';
    if (row.status === 'draft') return 'draft';
    if ((row.type === 'coupon' || row.type === 'popin')) {
      if (row.payment_status === 'completed') return 'paid';
      if (row.payment_status === 'pending') return 'pending';
      return null;
    }
    // Plain leads fall back to their own status field.
    if (row.status === 'pending') return 'pending';
    return null;
  };

  // Bucket a row into one of the lead type filters.
  const getTypeKey = (row) => {
    if (!row) return 'lead';
    if (row.type === 'coupon') return 'coupon';
    if (row.type === 'popin') return row.coupon_id ? 'popin_coupon' : 'popin';
    return 'lead';
  };

  // The date shown in the "Date" column (and used for sorting).
  // Use the payment date when available; otherwise fall back to the creation
  // date so draft/pending rows (which have no payment date) still show a date.
  const getSubmittedRaw = (row) => row?.payment_date || row?.created_at || null;

  const formatSubmittedDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  // Urgency colour based on how long ago the payment was made.
  const getPaymentUrgency = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    const hours = (Date.now() - d.getTime()) / 3600000;
    if (hours < 1) return 'green';
    if (hours < 4) return 'orange';
    if (hours < 8) return 'red';
    return 'overdue';
  };

  const getStatusCount = (key) => couponsData.filter((d) => getStatusKey(d) === key).length;

  const getTypeCount = (key) =>
    couponsData.filter((d) =>
      (statusFilter.length === 0 || statusFilter.includes(getStatusKey(d))) && getTypeKey(d) === key
    ).length;

  const toggleStatusFilter = (key) => {
    setStatusFilter((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const toggleTypeFilter = (key) => {
    setTypeFilter((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const getAllCoupons = () => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URI}/v3/leads`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: "Bearer " + authCtx.token }
    })
    .then(response => {
      return response.json();
    }).then(data => {
      if (data.status === 'success') {
        const normalized = (data?.data || []).map(normalizeLeadRow);
        setCouponsData(normalized);
        setLoading(false);
      } else {
        console.error("API Error:", data.message || "Failed to fetch coupons");
        setLoading(false);
      }
    }).catch(err => {
      console.error("Error fetching coupons:", err);
      setLoading(false);
    });
  }

  useEffect(() => {
    getAllCoupons();
  }, []);

  useEffect(() => {
    if (drawerCtx.pageUpdate?.page === "CouponLeads") {
      drawerCtx.setPageUpdate({ page: "" });
      getAllCoupons();
    }
  }, [drawerCtx.pageUpdate, authCtx.token]);

  useEffect(() => {
    if (modalCtx.pageUpdate?.page === "CouponLeads") {
      const couponId = modalCtx.pageUpdate?.couponId;
      const itemId = modalCtx.pageUpdate?.itemId;
      modalCtx.setPageUpdate({ page: "" });

      if (couponId) {
        // Targeted update: mark the published row as live without refetching everything
        setCouponsData((prev) =>
          prev.map((item) =>
            item.id === couponId && item.type === 'coupon'
              ? { ...item, status: 'approved' }
              : item
          )
        );
      } else if (itemId) {
        // After delete (or other updates), just refetch for correctness.
        getAllCoupons();
      } else {
        getAllCoupons();
      }
    }
  }, [modalCtx.pageUpdate, authCtx.token]);

  useEffect(() => {
    // Filter by selected status buckets (multi-select; empty = all statuses).
    const statusFiltered = statusFilter.length === 0
      ? couponsData
      : couponsData.filter((data) => statusFilter.includes(getStatusKey(data)));

    // Filter by selected lead types (multi-select; empty = all types).
    const typeFiltered = typeFilter.length === 0
      ? statusFiltered
      : statusFiltered.filter((data) => typeFilter.includes(getTypeKey(data)));

    const query = searchInput.trim();
    let searchFiltered = typeFiltered;
    let fuzzy = false;
    let suggestions = [];
    const related = {};

    if (query) {
      const q = normalizeText(query);
      const qName = normalizeCompact(query);
      const qSite = normalizeWebsite(query);

      const matchesRow = (row, matcher) => {
        const f = getLeadSearchFields(row);
        return matcher(qName, f.name) || matcher(q, f.email) || matcher(qSite, f.website);
      };

      let matches = typeFiltered.filter((row) => matchesRow(row, exactMatch));

      if (matches.length === 0) {
        const scored = [];
        matches = typeFiltered.filter((row) => {
          const f = getLeadSearchFields(row);
          const candidates = [
            { raw: row.companyName || row.company_name, norm: f.name, q: qName },
            { raw: row.email, norm: f.email, q },
            { raw: row.website, norm: f.website, q: qSite },
          ].filter((c) => c.raw && isFuzzyMatch(c.q, c.norm));
          if (candidates.length === 0) return false;
          candidates.forEach((c) => scored.push({ raw: c.raw, dist: editDistance(c.q, c.norm).prefix }));
          return true;
        });
        fuzzy = matches.length > 0;

        scored.sort((a, b) => a.dist - b.dist);
        const seen = new Set();
        for (const s of scored) {
          const key = normalizeText(s.raw);
          if (seen.has(key)) continue;
          seen.add(key);
          suggestions.push(s.raw);
          if (suggestions.length === 5) break;
        }
      }
      
      const emails = new Set(matches.map((r) => normalizeText(r.email)).filter(Boolean));
      const sites = new Set(matches.map((r) => normalizeWebsite(r.website)).filter(Boolean));
      const matchedKeys = new Set(matches.map(getRowKey));
      const relatedRows = typeFiltered.filter((row) => {
        if (matchedKeys.has(getRowKey(row))) return false;
        const email = normalizeText(row.email);
        const site = normalizeWebsite(row.website);
        if (email && emails.has(email)) { related[getRowKey(row)] = 'email'; return true; }
        if (site && sites.has(site)) { related[getRowKey(row)] = 'website'; return true; }
        return false;
      });

      searchFiltered = [...matches, ...relatedRows];
    }

    setSearchMeta({ fuzzy, suggestions, related });

    // Sort by date:
    //  - When only Live is selected: date DESC (most recently made live first)
    //  - Otherwise: date ASC (oldest/most urgent first)
    const liveOnly = statusFilter.length === 1 && statusFilter[0] === 'live';
    const sorted = [...searchFiltered].sort((a, b) => {
      if (liveOnly) {
        return new Date(getSubmittedRaw(b)) - new Date(getSubmittedRaw(a));
      }
      return new Date(getSubmittedRaw(a)) - new Date(getSubmittedRaw(b));
    });

    setDisplayedData(sorted);
    setVisibleItems(20);
  }, [searchInput, loading, couponsData, statusFilter, typeFilter]);

  // Infinite scroll handler.
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = tableContainer;
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        setVisibleItems((prev) => (prev < displayedData.length ? prev + 20 : prev));
      }
    };

    tableContainer.addEventListener('scroll', handleScroll);
    return () => tableContainer.removeEventListener('scroll', handleScroll);
  }, [displayedData.length]);

  const handleModal = (modal, data) => {
    handleClose();
    modalCtx.openModalWithDetails(modal, data);
  }

  function toggleDrawerHandler(drawer, coupon) {
    drawerCtx.openDrawer();
    drawerCtx.setDetails(drawer, coupon);
  };

  const getCouponLeadById = async (id, type) => {
    setLoadingView(true);
    try {
      // Add type as query parameter so API knows whether it's a lead or coupon
      const url = `${process.env.REACT_APP_API_URI}/v3/leads/${id}${type ? `?type=${type}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: "Bearer " + authCtx.token
        }
      });

      const data = await response.json();

      if (data.status === 'success') {
        setLoadingView(false);
        // API returns { id, type, entity } for typed lookups.
        const payload = data?.data;
        if (payload && payload.entity) return payload.entity;
        return payload || null;
      } else {
        console.error("API Error:", data.message || "Failed to fetch coupon lead");
        setLoadingView(false);
        return null;
      }
    } catch (err) {
      console.error("Error fetching coupon lead:", err);
      setLoadingView(false);
      return null;
    }
  };

  const handleViewClick = async () => {
    handleClose();
    if (!selectedCoupon?.id) {
      console.error("No coupon selected");
      return;
    }

    // Get the type from the selected item (either 'lead' or 'coupon')
    const itemType = selectedCoupon.type || 'lead'; // lead | coupon | popin
    const couponData = await getCouponLeadById(selectedCoupon.id, itemType);
    if (couponData) {
      // Use different drawer based on type
      const drawerName =
        itemType === 'coupon'
          ? 'view-coupon-lead'
          : itemType === 'popin'
            ? 'view-popin-lead'
            : 'view-lead';
      toggleDrawerHandler(drawerName, couponData);
    }
  };

  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.target);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusClick = (item) => {
    if (item.type === 'coupon' && item.payment_status === 'completed') {
      handleMakeItLiveClick(item);
    }
    if (item.type === 'popin' && item.payment_status === 'completed') {
      handleMakePopinLiveClick(item);
    }
  };

  const handleMakeItLiveClick = async (couponItem) => {
    const coupon = couponItem || selectedCoupon;
    handleClose();

    if (!coupon?.id) {
      modalCtx.openModalWithDetails('make-coupon-live', { coupon, checkError: 'Coupon ID is required' });
      return;
    }

    setCheckingMakeLive(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URI}/v3/advertising/coupon/${coupon.id}/companyCheck`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authCtx.token}`,
          },
        }
      );

      const json = await response.json();

      if (!response.ok) {
        const errorMsg =
          response.status === 403
            ? 'Invalid or missing coupon access token. Please re-authenticate or re-open the coupon flow.'
            : response.status === 404
              ? 'Advertising listing not found.'
              : json.error || 'Something went wrong. Please try again.';
        modalCtx.openModalWithDetails('make-coupon-live', { coupon, checkError: errorMsg });
        return;
      }

      if (json.status !== 'success' || !json.data) {
        modalCtx.openModalWithDetails('make-coupon-live', {
          coupon,
          checkError: 'Invalid response from server. Please try again.',
        });
        return;
      }

      const { companyExists, existingCompany, leadCompany, misalignedFields } = json.data;

      if (companyExists === true) {
        modalCtx.openModalWithDetails('company-already-exists', {
          existingCompany,
          leadCompany,
          misalignedFields: misalignedFields || [],
          coupon,
        });
      } else {
        const fullCoupon = await getCouponLeadById(coupon.id, 'coupon');
        const couponWithCode = fullCoupon ? { ...coupon, coupon: fullCoupon.coupon } : coupon;
        modalCtx.openModalWithDetails('make-coupon-live', { coupon: couponWithCode });
      }
    } catch (err) {
      console.error('Error during company check:', err);
      modalCtx.openModalWithDetails('make-coupon-live', {
        coupon,
        checkError: 'Something went wrong. Please try again.',
      });
    } finally {
      setCheckingMakeLive(false);
    }
  };

  const handleMakePopinLiveClick = async (popinItem) => {
    const popin = popinItem || selectedCoupon;
    handleClose();

    if (!popin?.id) {
      console.error('Popin ID is required');
      return;
    }

    setCheckingMakeLive(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URI}/v3/advertising/popin/${popin.id}/live`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authCtx.token}`,
          },
        }
      );

      const json = await response.json();
      if (!response.ok || json.status !== 'success') {
        console.error('Failed to make popin live:', json?.error || json?.message || 'Unknown error');
        return;
      }

      // Optimistically update status to live ('active' is the pop-in live status).
      setCouponsData((prev) =>
        prev.map((item) =>
          item.id === popin.id && item.type === 'popin'
            ? { ...item, status: 'active' }
            : item
        )
      );
    } catch (err) {
      console.error('Error making popin live:', err);
    } finally {
      setCheckingMakeLive(false);
    }
  };

  // Mark a pending inquiry (plain lead) as Contacted.
  const handleMarkAsContacted = async (leadItem) => {
    const lead = leadItem || selectedCoupon;
    handleClose();

    if (!lead?.id) {
      console.error('Lead ID is required');
      return;
    }

    setMarkingContacted(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URI}/v3/leads/${lead.id}/contacted`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authCtx.token}`,
          },
        }
      );

      const json = await response.json();
      if (!response.ok || json.status !== 'success') {
        const errorMsg = response.status === 404
          ? 'Lead not found.'
          : json?.error || json?.message || 'Unknown error';
        console.error('Failed to mark lead as contacted:', errorMsg);
        return;
      }

      // Optimistically update status to contacted.
      setCouponsData((prev) =>
        prev.map((item) =>
          item.id === lead.id && item.type !== 'coupon' && item.type !== 'popin'
            ? { ...item, status: 'contacted' }
            : item
        )
      );
    } catch (err) {
      console.error('Error marking lead as contacted:', err);
    } finally {
      setMarkingContacted(false);
    }
  };

  const renderStatus = (item) => {
    // For coupons & popins, prioritize payment_status; for leads, use status
    if ((item.type === 'coupon' || item.type === 'popin') && item.payment_status) {
      const paymentStatus = item.payment_status;

      // If status is draft, show draft badge
      if (item.status === 'draft') {
        return <div className="status-draft">Draft</div>;
      }

      // Live = coupon 'approved' or pop-in 'active' (see getStatusKey).
      if (getStatusKey(item) === 'live') {
        return <div className="status-live">Live</div>;
      }

      if (paymentStatus === 'completed') {
        return (
          <div
            className="status-active clickable"
            onClick={() => handleStatusClick(item)}
            title="Click to make live"
          >
            Paid
          </div>
        );
      }

      if (paymentStatus === 'pending') {
        return <div className="status-pending">Pending</div>;
      }

      if (paymentStatus === 'failed') {
        return <div className="status-deleted">Failed</div>;
      }

      if (paymentStatus === 'cancelled') {
        return <div className="status-suspended">Cancelled</div>;
      }

      // For other payment_status values
      return <div className="status-unknown">{paymentStatus}</div>;
    }

    // For leads or items without payment_status, use status field
    const status = item.status;

    if (status === 'draft') {
      return <div className="status-draft">Draft</div>;
    }

    if (status === 'pending') {
      return <div className="status-pending">Pending</div>;
    }

    if (status === 'contacted') {
      return <div className="status-invite">Contacted</div>;
    }

    if (status === 'completed') {
      return <div className="status-active">Completed</div>;
    }

    // For other status values
    return status ? <div className="status-unknown">{status}</div> : null;
  };

  // Statuses whose "Date Submitted" gets the urgency colour code.
  const URGENCY_STATUSES = ['paid', 'pending'];

  // Renders the "Date Submitted" cell. Paid & pending items get the urgency colour code.
  const renderDateSubmitted = (item) => {
    const raw = getSubmittedRaw(item);
    const formatted = formatSubmittedDate(raw);
    if (!formatted) return <span className="date-submitted">-</span>;

    if (URGENCY_STATUSES.includes(getStatusKey(item))) {
      const urgency = getPaymentUrgency(raw);
      return <span className={`date-submitted urgency-${urgency}`}>{formatted}</span>;
    }
    return <span className="date-submitted">{formatted}</span>;
  };

  const visibleData = displayedData.slice(0, visibleItems);

  return (
    <>
      <div className="coupon-leads-container">
        <div className='header-section'>
          <span className='title'>LEADS</span>
          <div className='header-items-div'>
            <PageSearchBar value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search leads" />
            <div className="status-btns">
              {STATUS_FILTERS.map(({ key, label }) => (
                <div
                  key={key}
                  className={`filter-item ${statusFilter.includes(key) ? 'selected' : ''}`}
                  onClick={() => toggleStatusFilter(key)}
                >
                  {label}({getStatusCount(key)})
                </div>
              ))}
            </div>
            <div className="status-btns">
              {TYPE_FILTERS.map(({ key, label }) => (
                <div
                  key={key}
                  className={`filter-item ${typeFilter.includes(key) ? 'selected' : ''}`}
                  onClick={() => toggleTypeFilter(key)}
                >
                  {label}({getTypeCount(key)})
                </div>
              ))}
            </div>
          </div>
        </div>
        {!loading && searchInput.trim() && searchMeta.fuzzy && (
          <div className="fuzzy-suggestion-bar">
            <span>No exact match for "{searchInput.trim()}". Did you mean:</span>
            {searchMeta.suggestions.map((suggestion) => (
              <Chip
                key={suggestion}
                size="small"
                label={suggestion}
                clickable
                className="suggestion-chip"
                onClick={() => setSearchInput(suggestion)}
              />
            ))}
          </div>
        )}
        <TableContainer
          component={Paper}
          className='listing-table company-table'
          ref={tableContainerRef}
          sx={{ overflow: 'auto' }}
        >
          <Table sx={{ minWidth: 500 }} stickyHeader>
            <TableHead>
               <TableRow className='table-head'>
                <TableCell className='head-cell company-head-cell' align="left" style={{ padding: 0, width: '6px', minWidth: '6px' }}>&nbsp;</TableCell>
                <TableCell className='head-cell company-head-cell' align="left">&nbsp;</TableCell>
                <TableCell align="left" className='head-cell company-head-cell'>Company</TableCell>
                <TableCell align="left" className='head-cell company-head-cell'>Type</TableCell>
                <TableCell align="left" className='head-cell company-head-cell'>Affiliate Code</TableCell>
                <TableCell align="left" className='head-cell company-head-cell'>Status</TableCell>
                <TableCell align="left" className='head-cell company-head-cell'>Date</TableCell>
                <TableCell align="left" className='head-cell company-head-cell'>Email</TableCell>
                <TableCell align="right" className='head-cell company-head-cell'></TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={loading ? "data-loading-circle" : "company-table-body"}>
              {
                loading ? <TableRow>
                    <TableCell colSpan={9} align="center" style={{ border: "0px", width: "100%", paddingTop: "100px" }}>
                      <CircularProgress size={52} style={{ color: "#2b1de8", border: "0px" }} />
                    </TableCell>
                  </TableRow> : visibleData.map((data, index) => {

                  const companyName = data.companyName || data.company_name || "N/A";
                  const typeLabel = getRowTypeLabel(data);
                  const isOverdue = URGENCY_STATUSES.includes(getStatusKey(data)) && getPaymentUrgency(getSubmittedRaw(data)) === 'overdue';

                  return (
                    <TableRow key={getRowKey(data)} className={`company-table-row ${isOverdue ? 'lead-row-overdue' : ''}`} style={{ backgroundColor: isOverdue ? undefined : (index % 2 != 0 ? '#fafafc' : 'white') }}>
                      <TableCell className='table-cell company-table-cell color-strip' style={{ '--strip-color': '#ffffff', padding: 0, width: '6px', minWidth: '6px' }} />
                      <TableCell className='table-cell company-table-cell' style={{ width: '50px', padding: 0, paddingLeft: '1rem' }} align="left">
                        <CompanyLogo logoUrl={getCompanyLogoUrl(data)} name={companyName} />
                      </TableCell>
                      <TableCell className='table-cell company-table-cell' style={{ flex: 1 }} align="left">
                        <div className="company-cell">
                          <div className="company-name">{companyName}</div>
                          {searchMeta.related[getRowKey(data)] && (
                            <Chip
                              size="small"
                              label={`Same ${searchMeta.related[getRowKey(data)]}`}
                              className="related-match-chip"
                              title="This company shares an email/website with a company matching your search"
                            />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className='table-cell company-table-cell' style={{ width: '140px' }} align="left">
                        {typeLabel && (
                          <Chip
                            size="small"
                            label={typeLabel}
                            variant="outlined"
                            className="lead-type-chip"
                          />
                        )}
                      </TableCell>
                      <TableCell className='table-cell company-table-cell' style={{ width: '120px' }} align="left">{data.affiliate?.code || '-'}</TableCell>
                      <TableCell className='table-cell company-table-cell' style={{ width: '140px' }} align="left">
                        <div className='status-container'>{renderStatus(data)}</div>
                      </TableCell>
                      <TableCell className='table-cell company-table-cell' style={{ width: '180px' }} align="left">
                        {renderDateSubmitted(data)}
                      </TableCell>
                      <TableCell className='table-cell company-table-cell' style={{ width: '180px' }} align="left">{data.email || 'N/A'}</TableCell>
                      <TableCell className='table-cell company-table-cell' style={{ width: '80px' }} align="left">
                        <div className='icons-container'>
                          <MoreVertRounded
                            aria-controls={open ? 'fade-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            onClick={(e) => {
                              setSelectedCoupon(data);
                              handleClick(e);
                            }}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {!loading && visibleData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" style={{ border: "0px", padding: "40px", color: "#9294a3" }}>
                    No leads found.
                  </TableCell>
                </TableRow>
              )}
              {!loading && visibleItems < displayedData.length && (
                <TableRow>
                  <TableCell colSpan={9} align="center" style={{ border: "0px", padding: "20px" }}>
                    <CircularProgress size={32} style={{ color: "#2b1de8" }} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <Menu
        id={`fade-menu`}
        MenuListProps={{ 'aria-labelledby': `fade-button` }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
      >
        <MenuItem
          onClick={handleViewClick}
          disabled={loadingView}
        >
          {loadingView ? 'Loading...' : 'View'}
        </MenuItem>
        {(selectedCoupon?.type === 'coupon' || selectedCoupon?.type === 'popin') && (
          <MenuItem
            onClick={() => selectedCoupon?.type === 'coupon' ? handleMakeItLiveClick(selectedCoupon) : handleMakePopinLiveClick(selectedCoupon)}
            disabled={selectedCoupon?.payment_status !== 'completed' || getStatusKey(selectedCoupon) === 'live' || checkingMakeLive}
          >
            {checkingMakeLive ? 'Checking...' : 'Make it Live'}
          </MenuItem>
        )}
        {selectedCoupon
          && selectedCoupon.type !== 'coupon'
          && selectedCoupon.type !== 'popin'
          && selectedCoupon.status === 'pending' && (
          <MenuItem
            onClick={() => handleMarkAsContacted(selectedCoupon)}
            disabled={markingContacted}
          >
            {markingContacted ? 'Marking...' : 'Mark as Contacted'}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            const modalData = (selectedCoupon?.type === 'coupon' || selectedCoupon?.type === 'popin')
              ? { coupon: selectedCoupon }
              : { lead: selectedCoupon };
            handleModal('delete-lead', modalData);
            handleClose();
          }}
          disabled={getStatusKey(selectedCoupon) === 'live'}
        >
          Delete
        </MenuItem>
      </Menu>

      <Snackbar open={checkingMakeLive} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="info" sx={{ width: '100%' }}>
          Checking company...
        </Alert>
      </Snackbar>
    </>
  );
}
